import React, { useState, useEffect } from 'react';
import { RouteComponentProps } from 'react-router';
import Title from '../../components/Title';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Formik } from 'formik';
import SubmitButton from '../../components/SubmitButton';
import UserContext from '../../contexts/UserContext';
import AddressLookup, { Address } from '../../components/AddressLookup';
import SmallButton from '../../components/SmallButton';
import { axiosKK } from '../../services/networkRequest';
import { UpdatePaymentRequest } from '../../models/APIModels';
import { PaymentFormErrors, PaymentFormInitialValues } from '../Payment';
import { LinkIcon } from '../../components/icons/LinkIcon';
import { KlarnaIcon } from '../../components/icons/KlarnaIcon';

interface PaymentProps extends RouteComponentProps {}

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY!!);

const cardInputStyle = {
  style: {
    base: {
      fontSize: '14px',
      color: '#424770',
      fontWeight: '500',
      fontFamily: 'Montserrat, sans-serif',
      '::placeholder': {
        color: '#9e9e9e',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

export default function UpdatePaymentComponent(
  props: PaymentProps,
): JSX.Element {
  return (
    <>
      <Elements stripe={stripePromise}>
        <PaymentComponent {...props} />
      </Elements>
    </>
  );
}

function PaymentComponent({ history }: PaymentProps): JSX.Element {
  const stripe = useStripe();
  const elements = useElements();
  const [billingAddress, setBillingAddress] = useState<Address>({});
  const [showComplete, setShowComplete] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const initialValues: PaymentFormInitialValues = {
    name: '',
    cardNumberValidation: false,
    cvcValidation: false,
    expiryDateValidation: false,
    address: '',
    agreeToTerms: true,
    agreeToPackingRules: true,
  };

  return (
    <>
      <section className="nav-status-bar">
        <div className="status-bar-content">
          <i className="fas fa-arrow-circle-left" />
          <SmallButton
            onClick={() => {
              history.push('/profile');
            }}
          >
            Back
          </SmallButton>
        </div>
      </section>
      <Title>Update Payment Method</Title>
      <UserContext.Consumer>
        {({ user }) => (
          <>
            {user && (
              <Formik
                initialValues={initialValues}
                validateOnMount={true}
                validate={({
                  name,
                  cardNumberValidation,
                  cvcValidation,
                  expiryDateValidation,
                }) => {
                  const errors: PaymentFormErrors = {};

                  if (!name || name?.length < 5) {
                    errors.name = 'too short of a name';
                  }

                  if (!cardNumberValidation) {
                    errors.cardNumberValidation = 'Card number incomplete';
                  }

                  if (!cvcValidation) {
                    errors.cardNumberValidation = 'CVC incomplete';
                  }

                  if (!expiryDateValidation) {
                    errors.cardNumberValidation = 'Expiry date incomplete';
                  }

                  return errors;
                }}
                onSubmit={async ({ name }, { setSubmitting }) => {
                  setSubmitting(true);

                  if (!stripe || !elements) {
                    // Stripe.js has not loaded yet. Make sure to disable
                    // form submission until Stripe.js has loaded.
                    throw new Error('Payment gateway not prepared');
                  }

                  // Get a reference to a mounted CardElement. Elements knows how
                  // to find your CardElement because there can only ever be one of
                  // each type of element.
                  const cardNumber = elements.getElement(CardNumberElement);
                  const cvcNumber = elements.getElement(CardCvcElement);
                  const expiryDate = elements.getElement(CardExpiryElement);

                  if (!(cardNumber && cvcNumber && expiryDate)) {
                    throw Error('Card elements not ready');
                  }

                  const { paymentMethod, error } =
                    await stripe.createPaymentMethod({
                      type: 'card',
                      card: cardNumber,
                      billing_details: {
                        name,
                        address: {
                          city: billingAddress.city,
                          line1: billingAddress.line1,
                          line2: billingAddress.line2,
                          postal_code: billingAddress.postCode,
                          state: billingAddress.county,
                        },
                      },
                    });

                  if (error) {
                    throw error;
                  }

                  if (!paymentMethod) {
                    throw new Error('Payment method not prepared');
                  }

                  const request: UpdatePaymentRequest = {
                    paymentMethodId: paymentMethod.id,
                  };

                  await axiosKK.put('/customer/addPaymentMethod', request);

                  setShowComplete(true);
                }}
              >
                {({
                  values,
                  handleChange,
                  handleSubmit,
                  isSubmitting,
                  isValid,
                  setFieldValue,
                }) => (
                  <form className="update-payment" onSubmit={handleSubmit}>
                    <section className="content">
                      <section className="all-details">
                        <div className="payment">
                          <div className="card-info">
                            <div className="title-bar">
                              <div className="title-with-icon">
                                <i className="fas fa-credit-card"></i>
                                <h5>Card Details</h5>
                              </div>
                              <div className="accepted-payment-methods">
                                <span className="payment-methods-label">We accept:</span>
                                <div className="payment-icons">
                                  <i className="fab fa-cc-visa" aria-label="Visa" style={{ color: '#1434CB' }} />
                                  <i className="fab fa-cc-mastercard" aria-label="Mastercard" style={{ color: '#EB001B' }} />
                                  <i className="fab fa-cc-amex" aria-label="American Express" style={{ color: '#006FCF' }} />
                                  <i className="fab fa-cc-apple-pay" aria-label="Apple Pay" style={{ color: '#000000' }} />
                                  <LinkIcon />
                                  <KlarnaIcon />
                                </div>
                              </div>
                            </div>
                            <div className="input-bar">
                              <div className="each">
                                <label className="payment-label">
                                  <i className="fas fa-user"></i> Cardholder Name
                                </label>
                                <section>
                                  <input
                                    onChange={handleChange}
                                    id="name"
                                    value={values.name}
                                    placeholder="John Smith"
                                  />
                                </section>
                              </div>
                              <div className="each">
                                <label className="payment-label">
                                  <i className="fas fa-credit-card"></i> Card Number
                                </label>
                                <section>
                                  <CardNumberElement
                                    options={cardInputStyle}
                                    onChange={(event) => {
                                      setFieldValue(
                                        'cardNumberValidation',
                                        event.complete,
                                      );
                                    }}
                                  />
                                </section>
                              </div>
                              <div className="each-tgt">
                                <div className="each">
                                  <label className="payment-label">
                                    <i className="fas fa-lock"></i> CVC
                                  </label>
                                  <section>
                                    <CardCvcElement
                                      options={cardInputStyle}
                                      onChange={(event) => {
                                        setFieldValue(
                                          'cvcValidation',
                                          event.complete,
                                        );
                                      }}
                                    />
                                  </section>
                                </div>
                                <div className="each">
                                  <label className="payment-label">
                                    <i className="fas fa-calendar-alt"></i> Expiry Date
                                  </label>
                                  <section>
                                    <CardExpiryElement
                                      options={cardInputStyle}
                                      onChange={(event) => {
                                        setFieldValue(
                                          'expiryDateValidation',
                                          event.complete,
                                        );
                                      }}
                                    />
                                  </section>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="security-note">
                            <i className="fas fa-shield-alt"></i>
                            <span>Your payment information is encrypted and secure</span>
                          </div>
                        </div>
                        <div className="address">
                          <div className="all-coll-del">
                            <AddressLookup
                              address={billingAddress}
                              setAddress={setBillingAddress}
                              label="Billing Address"
                            />
                          </div>
                        </div>
                      </section>
                    </section>
                    {showComplete && (
                      <div className="updateComplete">
                        <i className="fas fa-check-circle"></i>
                        <span>Payment details successfully updated!</span>
                      </div>
                    )}
                    <SubmitButton
                      className="payment-btn"
                      disabled={!isValid}
                      isSubmitting={isSubmitting}
                    >
                      <span>Update Card Details</span>
                      <i className="fas fa-arrow-right"></i>
                    </SubmitButton>
                  </form>
                )}
              </Formik>
            )}
          </>
        )}
      </UserContext.Consumer>
    </>
  );
}
